import React, { useCallback, useEffect, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { message } from "antd";

const FirstPartyDataDetailController = (props) => {
  const {
    children,
    activateHashedEmailList,
    refetchHashedEmailList,
    refetchHashedEmailLists,
    deleteHashedEmailList,
    deleteManyHashedEmailAddresses,
    processDeviceIds,
    updateHashedEmailList,
    updateHashedEmailLatestStatus,
    refreshAudienceCsv,
    processModelScore,
    currentOrg,
    providersVisible,
  } = props;

  const { id } = useParams();
  const history = useHistory();

  const [hashedEmailList, setHashedEmailList] = useState({});
  const [loading, setLoading] = useState(false);
  const [backLoading, setBackLoading] = useState(false);
  const [processModalVisible, setProcessModalVisible] = useState(false);
  const [step2DspModalVisible, setStep2DspModalVisible] = useState(false);
  const [selectedDspProvider, setSelectedDspProvider] = useState("liquidm");
  const [step2DspProvider, setStep2DspProvider] = useState("liquidm");
  const [processingModelScore, setProcessingModelScore] = useState(false);
  const [modelScoreResultsModal, setModelScoreResultsModal] = useState(false);
  const [modelScoreResults, setModelScoreResults] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [statusResultsModal, setStatusResultsModal] = useState(false);
  const [statusResults, setStatusResults] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  // Auto-switch to Nexxen if phone numbers or lookalike are detected since LiquidM doesn't support them
  useEffect(() => {
    if (hashedEmailList.typeOfFirstPartyData === "phones" || hashedEmailList.typeOfFirstPartyData === "lookalike") {
      if (selectedDspProvider === "liquidm") {
        setSelectedDspProvider("nexxen");
      }
      if (step2DspProvider === "liquidm") {
        setStep2DspProvider("nexxen");
      }
    }
  }, [hashedEmailList.typeOfFirstPartyData, selectedDspProvider, step2DspProvider]);

  const updateProgress = async (stepProgress, status, dsp = null) => {
    if (!hashedEmailList.id) return;

    try {
      await updateHashedEmailList({
        id: hashedEmailList.id,
        stepProgress,
        status,
        ...(dsp && { dsp: dsp.toUpperCase() }),
      });
      await handleLoadData();
    } catch (error) {
      console.error("Error updating progress:", error);
    }
  };

  const handleLoadData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await refetchHashedEmailList({
        id,
      });

      if (response && response.unauthorized) {
        message.warning(response.message);
        history.push("/audience/main/1");
        return;
      }

      if (response && response.data) {
        const hashedEmailListResult = response.data.hashedEmailLists[0];
        const dspSegmentIds = hashedEmailListResult.dspSegmentIds[0];
        const emailCount = hashedEmailListResult?.tag ? parseInt(hashedEmailListResult?.tag, 10) : 0;
        const status = hashedEmailListResult.status
          ? hashedEmailListResult.status
              .replace(/_/g, " ")
              .toLowerCase()
              .replace(/\b\w/g, (l) => l.toUpperCase())
          : hashedEmailListResult.activated
            ? "Activated"
            : "No Status";
        const stepProgress = hashedEmailListResult.stepProgress || 0;
        const matchRate =
          typeof hashedEmailListResult.matchRate === "number" &&
          Number.isFinite(hashedEmailListResult.matchRate)
            ? `${hashedEmailListResult.matchRate.toFixed(2).toLocaleString()}%`
            : "0%";
        const deviceCount =
          typeof hashedEmailListResult.deviceCount === "number"
            ? hashedEmailListResult.deviceCount
            : null;
        const orgName = hashedEmailListResult.orgs[0].name;

        setHashedEmailList({
          ...hashedEmailListResult,
          ...dspSegmentIds,
          emailCount,
          deviceCount,
          status,
          matchRate,
          orgName,
          stepProgress,
        });
      }
    } catch (error) {
      message.error(`Error loading data ${error.message}`);
    } finally {
      setLoading(false);
    }
  }, [history, id, refetchHashedEmailList]);

  useEffect(() => {
    handleLoadData();
  }, [handleLoadData]);

  const handleStep2DspSelection = async () => {
    try {
      setStep2DspModalVisible(false);
      await handleActivateEmails(step2DspProvider);
    } catch (error) {
      console.error("Error in Step 2 DSP selection:", error);
    }
  };

  const handleActivateEmails = async (dspProvider = null) => {
    try {
      setLoading(true);
      const response = await activateHashedEmailList({
        variables: {
          orgID: currentOrg?.id,
          hashedEmailListIds: [hashedEmailList.id],
          dsp: (dspProvider || step2DspProvider).toUpperCase(),
        }
      });

      if (
        response &&
        response.data &&
        response.data.activateHashedEmailList &&
        response.data.activateHashedEmailList.success
      ) {
        const dsp = dspProvider || step2DspProvider;
        await updateProgress(2, "WAITING_FOR_ACTIVATION", dsp.toUpperCase());
        message.success("Device ID mapping completed successfully!");
        await handleLoadData();
      } else if (
        response &&
        response.data &&
        response.data.activateHashedEmailList &&
        response.data.activateHashedEmailList.error
      ) {
        message.error(response.data.activateHashedEmailList.error);
      } else {
        message.error(
          "No device IDs were processed. Please make sure there are emails to process."
        );
      }
    } catch (error) {
      message.error(`Error activating emails ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGetLatestStatus = async () => {
    try {
      setStatusLoading(true);

      if (!hashedEmailList.id) {
        message.error("Audience not found");
        return;
      }

      const response = await updateHashedEmailLatestStatus({
        variables: {
          orgID: currentOrg?.id,
          hashedEmailListIds: [hashedEmailList.id],
        },
      });

      if (response && response.data && response.data.updateHashedEmailLatestStatus) {
        let result;
        try {
          if (typeof response.data.updateHashedEmailLatestStatus === "string") {
            result = JSON.parse(response.data.updateHashedEmailLatestStatus);
          } else {
            result = response.data.updateHashedEmailLatestStatus;
          }
        } catch (parseError) {
          console.error("Error parsing response:", parseError);
          message.error("Error parsing server response");
          return;
        }

        if (result && (result.success || result.data)) {
          const responseMessage = result.message || "Status updated successfully!";
          message.success(responseMessage);

          let modalData;
          if (result.data && Array.isArray(result.data)) {
            modalData = {
              success: true,
              message: responseMessage,
              processed: result.data.length,
              successCount: result.data.length,
              errorCount: 0,
              hashedEmailLists: result.data.map((item) => ({
                id: item.id,
                name: item.name,
                stepProgress: item.stepProgress,
                status: item.status,
                dsp: item.dsp,
                dspSegmentIds: item.dspSegmentIds || [],
                devicesCount: item.devicesCount,
                createdAt: item.createdAt,
                message: item.message,
                processResult: item.message,
                typeOfFirstPartyData: item.typeOfFirstPartyData,
                lastStepProgress: item.lastStepProgress,
                lastStatus: item.lastStatus,
              })),
            };
          } else {
            modalData = {
              success: true,
              message: responseMessage,
              processed: 1,
              successCount: 1,
              errorCount: 0,
              hashedEmailLists: [
                {
                  id: hashedEmailList.id,
                  name: hashedEmailList.name,
                  stepProgress: result.stepProgress || hashedEmailList.stepProgress,
                  status: result.status || hashedEmailList.status,
                  dsp: hashedEmailList.dsp,
                  dspSegmentIds: hashedEmailList.dspSegmentIds || [],
                  devicesCount: hashedEmailList.devicesCount || hashedEmailList.deviceCount,
                  createdAt: hashedEmailList.createdAt,
                  message: result.message,
                  processResult: result.message || "Status updated successfully",
                  typeOfFirstPartyData: result.typeOfFirstPartyData,
                  lastStepProgress: result.lastStepProgress,
                  lastStatus: result.lastStatus,
                },
              ],
            };
          }

          setStatusResults(modalData);
          setStatusResultsModal(true);
          await handleLoadData();
        } else {
          message.error(result?.error || "Failed to update status");
        }
      } else {
        message.warning("No response received from server");
      }
    } catch (error) {
      message.error(`Error updating latest status: ${error.message}`);
      console.error("Error in handleGetLatestStatus:", error);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleProcessDeviceIds = async (dspProvider) => {
    try {
      setLoading(true);
      setProcessModalVisible(false);

      const response = await processDeviceIds({
        variables: {
          orgID: currentOrg?.id,
          emailListIds: [hashedEmailList.id],
          dsp: dspProvider.toUpperCase(),
        },
      });

      if (response && response.data.processDeviceIds.deviceIdsProcessed > 0) {
        await updateProgress(3, "WAITING_FOR_ACTIVATION", dspProvider.toUpperCase());
        message.success("Email list processed.");
      } else {
        message.warning("No device IDs were processed.");
      }
    } catch (error) {
      message.error(`Error processing device ID's ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleBackNavigation = async () => {
    try {
      setBackLoading(true);
      if (refetchHashedEmailLists) {
        await refetchHashedEmailLists();
      }
    } catch (error) {
      console.error("Error refetching email lists:", error);
    } finally {
      setBackLoading(false);
      history.push("/audience/main/1");
    }
  };

  const handleDeleteAudience = async () => {
    try {
      setLoading(true);

      if (
        hashedEmailList.status === "WAITING_FOR_ACTIVATION" ||
        hashedEmailList.status === "ACTIVATED" ||
        (hashedEmailList.stepProgress && hashedEmailList.stepProgress >= 3)
      ) {
        message.error("Cannot delete an audience that is in progress or already activated");
        return;
      }

      try {
        await deleteManyHashedEmailAddresses({
          variables: { id: hashedEmailList.id },
        });
      } catch (err) {
        console.warn('Failed to delete hashed email addresses:', err.message);
      }
      await deleteHashedEmailList({
        variables: { id: hashedEmailList.id },
      });
      message.success("Audience deleted, redirecting to main page...");
      setTimeout(() => {
        handleBackNavigation();
      }, 1000);
    } catch (err) {
      message.error(`Error deleting audience ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessModelScore = async () => {
    try {
      setProcessingModelScore(true);

      if (!currentOrg || !currentOrg.id) {
        message.error("Organization information not available");
        return;
      }

      const response = await processModelScore({
        variables: {
          orgID: currentOrg.id,
          hashedEmailListId: hashedEmailList.id,
        },
      });

      if (response && response.data && response.data.processModelScore) {
        let result;

        if (typeof response.data.processModelScore === "string") {
          try {
            result = JSON.parse(response.data.processModelScore);
          } catch (parseError) {
            console.error("Error parsing response:", parseError);
            result = {
              success: false,
              message: "Error parsing server response",
              rawResponse: response.data.processModelScore,
            };
          }
        } else {
          result = response.data.processModelScore;
        }

        setModelScoreResults(result);
        setModelScoreResultsModal(true);

        await handleLoadData();
      } else {
        message.warning("No response received from server");
      }
    } catch (error) {
      message.error(`Error processing model score: ${error.message}`);
      console.error("Error in handleProcessModelScore:", error);
    } finally {
      setProcessingModelScore(false);
    }
  };

  const handleRefreshAudience = async () => {
    try {
      setRefreshing(true);

      if (!hashedEmailList.id) {
        message.error("Audience not found");
        return;
      }

      const response = await refreshAudienceCsv({
        variables: {
          id: hashedEmailList.id,
          orgID: currentOrg?.id,
        },
      });

      if (response && response.data && response.data.refreshAudienceCsv) {
        let result;
        try {
          if (typeof response.data.refreshAudienceCsv === "string") {
            result = JSON.parse(response.data.refreshAudienceCsv);
          } else {
            result = response.data.refreshAudienceCsv;
          }
        } catch (parseError) {
          console.warn("Could not parse refreshAudienceCsv response:", parseError);
          result = { success: true, message: "Refresh triggered successfully" };
        }

        if (result && result.success !== false) {
          message.success(result.message || "Audience refresh triggered. CSVs are being re-uploaded to Nexxen.");
          await handleLoadData();
        } else {
          message.error(result?.error || result?.message || "Failed to trigger audience refresh");
        }
      } else {
        message.warning("No response received from server");
      }
    } catch (error) {
      message.error(`Error refreshing audience: ${error.message}`);
      console.error("Error in handleRefreshAudience:", error);
    } finally {
      setRefreshing(false);
    }
  };

  return React.cloneElement(children, {
    ...children.props,
    hashedEmailList,
    loading,
    backLoading,
    processModalVisible,
    setProcessModalVisible,
    step2DspModalVisible,
    setStep2DspModalVisible,
    selectedDspProvider,
    setSelectedDspProvider,
    step2DspProvider,
    setStep2DspProvider,
    processingModelScore,
    modelScoreResultsModal,
    setModelScoreResultsModal,
    setModelScoreResults,
    modelScoreResults,
    statusLoading,
    statusResultsModal,
    setStatusResultsModal,
    setStatusResults,
    statusResults,
    handleStep2DspSelection,
    handleActivateEmails,
    handleGetLatestStatus,
    handleProcessDeviceIds,
    handleBackNavigation,
    handleDeleteAudience,
    handleProcessModelScore,
    handleRefreshAudience,
    refreshing,
    providersVisible,
    currentOrg,
  });
};

export default FirstPartyDataDetailController;
